import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Schedules from "./pages/Schedules";
import DataExplorer from "./pages/DataExplorer";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/admin/AdminDashboard";
import FirstLoginChangePassword from "./pages/FirstLoginChangePassword";
import VerifyInitialCode from "./pages/VerifyInitialCode";
import ForgotPassword from "./pages/ForgotPassword";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RoleGuard } from "@/components/RoleGuard";
import Navbar from "@/components/Navbar";

const queryClient = new QueryClient();

function AppRoutes() {
  const location = useLocation();
  const { user } = useAuth();

  const onLoginPage = location.pathname === "/";
  const onForcedPage = location.pathname === "/first-login-change-password";
  const onVerifyPage = location.pathname === "/verify-initial-code";
  const onForgotPage = location.pathname === "/forgot-password";

  const mustForce = !!user?.mustChangePassword;
  const mustVerify = (localStorage.getItem("must_verify_code") || "0") === "1";

  // ✅ IMPORTANT : ne rediriger que si l'utilisateur est authentifié
  const isAuthed = !!localStorage.getItem("auth_token");

  if (isAuthed && !onForgotPage) {
    if (mustForce && mustVerify && !onVerifyPage) {
      return <Navigate to="/verify-initial-code" replace />;
    }
    if (mustForce && !mustVerify && !onForcedPage) {
      return <Navigate to="/first-login-change-password" replace />;
    }
  }

  const hideNavbar = onLoginPage || onForcedPage || onVerifyPage || onForgotPage;

  return (
    <>
      {!hideNavbar && <Navbar />}
      <Routes>
        {/* Public */}
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Protégées */}
        <Route
          path="/dashboard"
          element={
            <RoleGuard allow={["User", "Manager", "Admin"]}>
              <Dashboard />
            </RoleGuard>
          }
        />
        <Route
          path="/profile"
          element={
            <RoleGuard allow={["User", "Manager", "Admin"]}>
              <Profile />
            </RoleGuard>
          }
        />
        <Route
          path="/schedules"
          element={
            <RoleGuard allow={["Admin"]}>
              <Schedules />
            </RoleGuard>
          }
        />
        <Route
          path="/data-explorer"
          element={
            <RoleGuard allow={["Admin", "Manager"]}>
              <DataExplorer />
            </RoleGuard>
          }
        />

        {/* Forçage 1er login */}
        <Route
          path="/verify-initial-code"
          element={
            <RoleGuard allow={["User", "Manager", "Admin"]}>
              <VerifyInitialCode />
            </RoleGuard>
          }
        />
        <Route
          path="/first-login-change-password"
          element={
            <RoleGuard allow={["User", "Manager", "Admin"]}>
              <FirstLoginChangePassword />
            </RoleGuard>
          }
        />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <RoleGuard allow={["Admin"]}>
              <AdminDashboard />
            </RoleGuard>
          }
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
